<template>
  <div>
    <Layout>
      <template #header>
        <div class="mt-8 max-w-3xl mx-auto lg:max-w-7xl flex justify-end">
          <TeammateActions style="z-index: 40" :record="contact" @reload="getReload" />
        </div>
      </template>
      <div class="mt-8 max-w-3xl mx-auto grid grid-cols-1 gap-6 lg:max-w-7xl lg:grid-flow-col-dense lg:grid-cols-3">
        <div class="space-y-6 lg:col-start-1 lg:col-span-2">
          <Card>
            <div class="px-4 py-5 border-b border-gray-200 sm:px-6 flex flex-wrap justify-between">
              <div>
                <h3 class="inline-block text-lg leading-6 font-medium text-gray-900">
                  {{ contact.name }}
                </h3>
              </div>
              <p class="mt-1 max-w-2xl text-sm leading-5 text-gray-500">
                <span
                  v-if="contact.status == 'Active'"
                  class="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800"
                >
                  {{ contact.status }}
                </span>
                <span
                  v-else
                  class="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-red-100 text-red-800"
                >
                  {{ contact.status }}
                </span>
              </p>
            </div>
            <div class="px-4 py-5 sm:px-6" :class="{ 'border-b border-gray-200': customFieldsForEntity.length > 0 }">
              <dl class="grid grid-cols-1 gap-x-4 gap-y-8 sm:grid-cols-2">
                <div class="sm:col-span-1">
                  <dt class="text-sm leading-5 font-medium text-gray-500">Name</dt>
                  <dd class="mt-1 text-sm leading-5 text-gray-900">
                    {{ contact.name }}
                  </dd>
                </div>
                <div class="sm:col-span-1">
                  <dt class="text-sm leading-5 font-medium text-gray-500">Email</dt>
                  <dd class="mt-1 text-sm leading-5 text-gray-900">
                    <a class="text-indigo-600 hover:text-indigo-900" :href="`mailto:${contact.email}`">{{
                      contact.email
                    }}</a>
                  </dd>
                </div>
                <div class="sm:col-span-1">
                  <dt class="text-sm leading-5 font-medium text-gray-500">Phone</dt>
                  <dd class="mt-1 text-sm leading-5 text-gray-900">
                    <a class="text-indigo-600 hover:text-indigo-900" :href="`tel:${contact.phone}`">{{
                      contact.phone
                    }}</a>
                  </dd>
                </div>
                <div class="sm:col-span-1">
                  <dt class="text-sm leading-5 font-medium text-gray-500">RE License Number</dt>
                  <dd class="mt-1 text-sm leading-5 text-gray-900">
                    {{ contact.license }}
                  </dd>
                </div>
                <div class="sm:col-span-2">
                  <dt class="text-sm leading-5 font-medium text-gray-500">Referral Link</dt>
                  <dd class="mt-1 text-sm leading-5 text-gray-900">
                    <a :href="contact.referralLink" class="text-indigo-600 hover:text-indigo-900" target="_blank">
                      {{ contact.referralLink }}
                    </a>
                  </dd>
                </div>
                <div class="sm:col-span-1">
                  <dt class="text-sm leading-5 font-medium text-gray-500">Pipeline Reviewer</dt>
                  <dd class="mt-1 text-sm leading-5 text-gray-900">
                    {{ contact.assignedPipelineReviewer }}
                  </dd>
                </div>
                <div class="sm:col-span-1">
                  <dt class="text-sm leading-5 font-medium text-gray-500">Pipeline Review Cadence</dt>
                  <dd class="capitalize mt-1 text-sm leading-5 text-gray-900">
                    {{ contact.pipelineReviewCadence }}
                  </dd>
                </div>
              </dl>
            </div>
            <div v-if="customFieldsForEntity.length > 0" class="px-4 py-5 sm:px-6">
              <dl class="grid grid-cols-1 gap-x-4 gap-y-8 sm:grid-cols-2">
                <div
                  v-for="customField in customFieldsForEntity"
                  :key="customField.id"
                  :class="customField.field_type == 'rich_text' ? 'sm:col-span-2' : 'sm:col-span-1'"
                >
                  <dt class="text-sm leading-5 font-medium text-gray-500">
                    {{ customField.name }}
                  </dt>
                  <dd class="mt-1 text-sm leading-5 text-gray-900">
                    <ValueViewerAndEditor
                      :custom-field="customField"
                      :custom-field-value="contact.customFields.find(f => f.custom_field_id === customField.id)"
                      custom-fieldable-type="Contact"
                      :custom-fieldable-id="contact.id"
                      :method="'both'"
                      :display-submit-button="true"
                      @updated="handleCustomFieldUpdate"
                    />
                  </dd>
                </div>
              </dl>
            </div>
          </Card>

          <div
            v-if="Object.keys(contact.customAttributes).length > 0"
            class="mt-6 bg-white shadow overflow-hidden sm:rounded-lg"
          >
            <div class="px-4 py-5 border-b border-gray-200 sm:px-6 flex flex-wrap justify-between">
              <div>
                <h3 class="inline-block text-lg leading-6 font-medium text-gray-900">Custom fields</h3>
              </div>
            </div>
            <div class="px-4 py-5 sm:px-6">
              <dl class="grid grid-cols-1 gap-x-4 gap-y-8 sm:grid-cols-4">
                <div v-for="key in Object.keys(contact.customAttributes)" :key="key" class="sm:col-span-1">
                  <dt class="text-sm leading-5 font-medium text-gray-500">
                    {{ key }}
                  </dt>
                  <dd class="mt-1 text-sm leading-5 text-gray-900">
                    {{ contact.customAttributes[key] ? contact.customAttributes[key] : 'null' }}
                  </dd>
                </div>
              </dl>
            </div>
          </div>

          <Card class="px-4 py-5 sm:px-6" extra-classes="overflow-visible">
            <MenuTabs :tabs="['Activity', 'Pipeline Reviews', 'Events']" @selected-tab="handleSelectedTab" />
            <TeammatePipelineReviews
              v-if="selectedTab == 'Pipeline Reviews'"
              :contact="contact"
              @refresh-contact="getContact()"
            />
            <TeammateActivity
              v-if="selectedTab == 'Activity'"
              :contact="contact"
              @activity-created="getNotesAndEvents()"
              @activity-deleted="getNotesAndEvents()"
            />
            <TeammateEvents v-if="selectedTab == 'Events'" :contact="contact" />
          </Card>
        </div>

        <div>
          <section aria-labelledby="enrichment-title">
            <TeammateEnrichment v-if="loaded" :contact="contact" />
          </section>
          <section aria-labelledby="referrals-title" class="lg:col-start-3 lg:col-span-1">
            <TeammateReferrals :contact="contact" />
          </section>
          <section aria-labelledby="attachments-title" class="mt-6 lg:col-start-3 lg:col-span-1">
            <TeammateAttachments :contact="contact" @reload="getReload" />
          </section>
        </div>
      </div>

      <div
        v-if="$route.query.history"
        class="mt-6 max-w-3xl mx-auto grid grid-cols-1 gap-6 lg:max-w-7xl lg:grid-flow-col-dense lg:grid-cols-1"
      >
        <TeammateHistory :contact="contact" />
      </div>
    </Layout>
  </div>
</template>

<script>
import Layout from '../Layout/index.vue';
import Card from '../Card/index.vue';
import MenuTabs from '../shared/MenuTabs.vue';
import TeammateActions from './TeammateActions.vue';
import TeammateReferrals from './TeammateReferrals.vue';
import TeammateAttachments from './TeammateAttachments.vue';
import TeammateActivity from './TeammateActivity.vue';
import TeammateEvents from './TeammateEvents.vue';
import TeammateHistory from './TeammateHistory.vue';
import TeammateEnrichment from './TeammateEnrichment.vue';
import TeammatePipelineReviews from './TeammatePipelineReviews.vue';
import ValueViewerAndEditor from '../custom_fields/ValueViewerAndEditor.vue';
import RailsUtility from '../../utilities/rails.ts';
import DateUtility from '../../utilities/dates.ts';
import { handleCustomFieldUpdate } from '../../utilities/custom_fields';

export default {
  components: {
    Layout,
    Card,
    MenuTabs,
    TeammateActions,
    TeammateReferrals,
    TeammateAttachments,
    TeammateActivity,
    TeammateEvents,
    TeammateHistory,
    TeammateEnrichment,
    TeammatePipelineReviews,
    ValueViewerAndEditor,
  },
  data() {
    return {
      contact: {
        name: '',
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        status: '',
        created: '',
        referralCode: '',
        referralLink: '',
        customAttributes: {},
        activities: [],
        files: [],
        history: [],
        referrals: [],
        assignedPipelineReviewer: '',
        customFields: [],
        events: [],
      },
      customFieldsForEntity: [],
      showCustomAttributes: false,
      showChat: false,
      loaded: false,
      selectedTab: 'Activity',
    };
  },
  mounted() {
    this.getContact();
    this.getNotesAndEvents();
  },
  methods: {
    ...DateUtility,
    handleCustomFieldUpdate(event) {
      handleCustomFieldUpdate(this.contact, event);
    },
    getReload() {
      this.getContact();
      this.getNotesAndEvents();
    },
    getContact() {
      const self = this;
      self.$store.commit('general/showLoadingSpinner');
      fetch(`/api/team/${self.$route.params.id}`)
        .then(response => {
          self.$store.commit('general/hideLoadingSpinner');
          return response.json();
        })
        .then(data => {
          self.contact.id = data.record.id;
          self.contact.name = data.record.name;
          self.contact.firstName = data.record.first_name;
          self.contact.lastName = data.record.last_name;
          self.contact.email = data.record.email;
          self.contact.phone = data.record.phone_number;
          self.contact.status = data.record.teammate_status;
          self.contact.license = data.record.re_license_number;
          self.contact.referralLink = data.referral_link;
          self.contact.customAttributes = data.record.jsonb;
          self.contact.created = this.formatDate(data.record.created_at, 'MMMM dd yyyy, h:mm a');
          self.contact.referralCode = data.record.referral_code;
          self.contact.files = data.files;
          self.contact.referrals = data.referrals;
          self.contact.assignedPipelineReviewer = data.record.assigned_pipeline_reviewer;
          self.contact.pipelineReviewCadence = RailsUtility.convertFromEnumFormat(data.record.pipeline_review_cadence);
          self.contact.customFields = data.record.custom_field_values || [];
          self.customFieldsForEntity =
            data.custom_fields_for_entity?.sort((a, b) => a.name.localeCompare(b.name)) || [];
          let contactHistory = [];
          for (let version of data.history) {
            if (version.version.object_changes != null) {
              for (let key of Object.keys(version.version.object_changes)) {
                if (key != 'updated_at') {
                  contactHistory.push({
                    'updated by': version.whodunnit,
                    'field changed': key.replaceAll('_', ' '),
                    from: version.version.object_changes[key][0],
                    to: version.version.object_changes[key][1],
                    'updated at': this.formatDate(version.version.created_at, 'MMMM dd yyyy, h:mm a'),
                  });
                }
              }
            }
          }
          self.contact.history = contactHistory;
          self.loaded = true;
        });
    },
    getNotesAndEvents() {
      const self = this;
      self.$store.commit('general/showLoadingSpinner');
      fetch(`/api/contact_notes/${self.$route.params.id}`)
        .then(response => {
          self.$store.commit('general/hideLoadingSpinner');
          return response.json();
        })
        .then(data => {
          self.getContactEmails(data);
        });
    },
    getContactEmails(notes) {
      const self = this;
      fetch(`/api/contact_emails/${self.$route.params.id}`)
        .then(response => {
          self.$store.commit('general/hideLoadingSpinner');
          return response.json();
        })
        .then(data => {
          self.getEvents(notes, data);
        });
    },
    getEvents(notes, emails) {
      const self = this;
      self.$store.commit('general/showLoadingSpinner');
      fetch(`/api/contact_events?contact_id=${self.$route.params.id}`)
        .then(response => {
          self.$store.commit('general/hideLoadingSpinner');
          return response.json();
        })
        .then(data => {
          let fullTimeline = [];
          let events = [];
          for (let note of notes) {
            fullTimeline.push({
              activityType: 'note',
              sortDate: note.created_at,
              ...note,
            });
          }
          for (let email of emails) {
            fullTimeline.push({
              activityType: 'email',
              sortDate: email.created_at,
              ...email,
            });
          }
          for (let event of data) {
            events.push({
              activityType: 'event',
              sortDate: event.start_time,
              ...event,
            });
          }
          fullTimeline.sort((a, b) => {
            return new Date(b.sortDate) - new Date(a.sortDate);
          });
          self.contact.events = events;
          self.contact.activities = fullTimeline;
        });
    },
    handleSelectedTab(tab) {
      this.selectedTab = tab;
    },
  },
};
</script>
